import { DatePicker, Form, Input, InputNumber, Select, Switch } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import type { FieldDefinitionDto, FieldOption } from '@crm/shared';
import { OrganizationSelect } from './OrganizationSelect';
import { ContactSelect } from './ContactSelect';
import { AgentSelect } from './AgentSelect';
import { LinksInput, toLinks } from './LinksField';
import { MentionTextArea } from './Mentions';
import { getDateFormat } from '../utils/format';
import { RELATION_KEYS, SYSTEM_PROPS, isEditable } from '../features/fields/system-keys';

/** Form.Item name path for a definition: system → API prop, custom → ['fields', key]. */
export function fieldName(def: FieldDefinitionDto): string | string[] {
  if (def.isSystem) return SYSTEM_PROPS[def.objectType][def.key] ?? def.key;
  return ['fields', def.key];
}

/** The input control for a field type. Receives value/onChange from Form.Item. */
export function DynamicInput({ def, ...rest }: { def: FieldDefinitionDto } & Record<string, unknown>) {
  const options = (def.options ?? []) as FieldOption[];
  if (RELATION_KEYS.has(def.key)) {
    if (def.key === 'organization_id') return <OrganizationSelect {...rest} />;
    if (def.key === 'contact_id') return <ContactSelect {...rest} />;
    if (def.key === 'agent_id') return <AgentSelect {...rest} />;
  }
  if (def.key === 'currency') {
    return <Select showSearch options={['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'CHF', 'JPY', 'AED', 'INR', 'SGD'].map((c) => ({ value: c, label: c }))} {...rest} />;
  }
  switch (def.type) {
    case 'TEXTAREA':
      return <MentionTextArea autoSize={{ minRows: 2, maxRows: 8 }} {...rest} />;
    case 'NUMBER':
      return <InputNumber style={{ width: '100%' }} {...rest} />;
    case 'CURRENCY':
      return <InputNumber style={{ width: '100%' }} min={0} precision={2} {...rest} />;
    case 'DATE':
      return <DatePicker style={{ width: '100%' }} {...rest} />;
    case 'DATETIME':
      return <DatePicker showTime style={{ width: '100%' }} {...rest} />;
    case 'BOOLEAN':
      return <Switch {...rest} />;
    case 'SELECT':
      return <Select allowClear options={options.map((o) => ({ value: o.value, label: o.label }))} {...rest} />;
    case 'MULTISELECT':
      // lists without fixed options (e.g. imported keywords) take any values
      return <Select mode={options.length ? 'multiple' : 'tags'} allowClear tokenSeparators={options.length ? undefined : [',']} options={options.map((o) => ({ value: o.value, label: o.label }))} {...rest} />;
    case 'LIST':
      return <Select mode="tags" allowClear tokenSeparators={[',']} placeholder="Type a value and press Enter" {...rest} />;
    case 'EMAIL':
      return <Input type="email" {...rest} />;
    case 'URL':
      return <Input placeholder="https://" {...rest} />;
    case 'PHONE':
      return <Input type="tel" {...rest} />;
    case 'LINKS':
      return <LinksInput {...rest} />;
    default:
      return <Input {...rest} />;
  }
}

/** A Form.Item wired for the field's type (valuePropName for switches, rules for required). */
export function FieldFormItem({ def, disabled }: { def: FieldDefinitionDto; disabled?: boolean }) {
  if (!isEditable(def)) return null;
  const rules = [];
  if (def.required) rules.push({ required: true, message: `${def.label} is required` });
  if (def.type === 'EMAIL') rules.push({ type: 'email' as const, message: 'Invalid email' });
  return (
    <Form.Item
      name={fieldName(def)}
      label={def.label}
      rules={rules}
      valuePropName={def.type === 'BOOLEAN' ? 'checked' : 'value'}
      tooltip={def.description ?? undefined}
    >
      <DynamicInput def={def} disabled={disabled} />
    </Form.Item>
  );
}

/** Converts an API record into form values (ISO dates → dayjs). */
export function toFormValues(defs: FieldDefinitionDto[], record: Record<string, unknown>): Record<string, unknown> {
  const values: Record<string, unknown> = { ...record, fields: { ...((record.fields as object) ?? {}) } };
  for (const def of defs) {
    if (def.type !== 'DATE' && def.type !== 'DATETIME') continue;
    const name = fieldName(def);
    const current = Array.isArray(name) ? (values.fields as Record<string, unknown>)[name[1]] : values[name];
    const converted = current ? dayjs(current as string) : undefined;
    if (Array.isArray(name)) (values.fields as Record<string, unknown>)[name[1]] = converted;
    else values[name] = converted;
  }
  return values;
}

/** Converts one input value into its API form (dayjs → ISO, blank → null, unset switch → false). */
export function toApiValue(def: FieldDefinitionDto, value: unknown): unknown {
  let v = value === undefined || value === '' ? null : value;
  if (def.type === 'BOOLEAN' && v === null) v = false;
  if (dayjs.isDayjs(v)) v = def.type === 'DATE' ? (v as Dayjs).format('YYYY-MM-DD') : (v as Dayjs).toISOString();
  return v;
}

/** Converts form values into an API payload (dayjs → ISO, undefined custom → null). */
export function fromFormValues(defs: FieldDefinitionDto[], values: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const fields: Record<string, unknown> = {};
  for (const def of defs) {
    if (!isEditable(def)) continue;
    const name = fieldName(def);
    const v = toApiValue(def, Array.isArray(name) ? (values.fields as Record<string, unknown> | undefined)?.[name[1]] : values[name]);
    if (Array.isArray(name)) fields[name[1]] = v;
    else out[name] = v;
  }
  out.fields = fields;
  return out;
}

/** Human-readable value for tables and detail views. */
export function formatValue(def: FieldDefinitionDto, value: unknown, dateFormat = getDateFormat()): string {
  if (value === null || value === undefined || value === '') return '';
  switch (def.type) {
    case 'DATE':
      return dayjs(value as string).format(dateFormat);
    case 'DATETIME':
      return dayjs(value as string).format(`${dateFormat} HH:mm`);
    case 'BOOLEAN':
      return value ? 'Yes' : 'No';
    case 'CURRENCY':
      return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    case 'NUMBER':
      return Number(value).toLocaleString();
    case 'SELECT': {
      const o = ((def.options ?? []) as FieldOption[]).find((x) => x.value === value);
      return o?.label ?? String(value);
    }
    case 'LINKS':
      return toLinks(value).map((l) => (l.label ? `${l.label}: ${l.url}` : l.url)).join(', ');
    case 'LIST':
      return (Array.isArray(value) ? value : [value]).map(String).join(', ');
    case 'MULTISELECT': {
      const opts = (def.options ?? []) as FieldOption[];
      return (Array.isArray(value) ? value : [value]).map((v) => opts.find((o) => o.value === v)?.label ?? String(v)).join(', ');
    }
    default:
      return String(value);
  }
}
